import React, { useState, useEffect, useCallback, useMemo } from "react";
import { Helmet } from "react-helmet";
import "./Hardware.css";

const hardwareByLocation = [
  {
    location: "US East",
    flag: "https://flagcdn.com/w40/us.png",
    probeUrl: "https://sky.sodiumhosting.com:8080",
    configs: [
      { cpu: "Intel Xeon V4", ram: "DDR4", storage: "SSD", singleCoreScore: 1024 },
      { cpu: "AMD EPYC SP5", ram: "DDR5", storage: "NVMe", singleCoreScore: 2547 },
    ],
  },
  {
    location: "Frankfurt, Germany",
    flag: "https://flagcdn.com/w40/de.png",
    probeUrl: "https://aston-game-ger.sodiumhosting.com:8080",
    configs: [
      { cpu: "AMD AM5", ram: "DDR5", storage: "NVMe", singleCoreScore: 2668 },
    ],
  },
  {
    location: "Australia",
    flag: "https://flagcdn.com/w40/au.png",
    probeUrl: "https://syd-au-ping.vultr.com",
    configs: [
      { cpu: "AMD EPYC SP5", ram: "DDR5", storage: "NVMe", singleCoreScore: 2547 },
    ],
  },
  {
    location: "Japan",
    flag: "https://flagcdn.com/w40/jp.png",
    probeUrl: "https://hnd-jp-ping.vultr.com",
    configs: [
      { cpu: "AMD EPYC SP5", ram: "DDR5", storage: "NVMe", singleCoreScore: 2547 },
    ],
  },
  {
    location: "United Kingdom",
    flag: "https://flagcdn.com/w40/gb.png",
    probeUrl: "https://lon-gb-ping.vultr.com",
    configs: [
      { cpu: "AMD EPYC SP5", ram: "DDR5", storage: "NVMe", singleCoreScore: 2547 },
    ],
  },
];


const measureLatency = (url, timeoutMs = 1200) => new Promise((resolve) => {
  const img = new Image();
  const started = performance.now();
  const timer = setTimeout(() => {
    img.src = "";
    resolve(null);
  }, timeoutMs);

  const finish = () => {
    clearTimeout(timer);
    resolve(Math.round(performance.now() - started));
  };

  img.onload = finish;
  img.onerror = finish;
  img.src = `${url}/favicon.ico?ts=${Date.now()}-${Math.random()}`;
});

const HardwareTier = ({ configs }) => (
  <div className="hardware-tier">
    <h3>Hardware profile</h3>
    {configs && configs.length > 0 ? (
      <div className="hardware-config-list">
        {configs.map((config, index) => (
          <article className="hardware-config" key={`${config.cpu}-${index}`}>
            <p><span>CPU:</span> {config.cpu}</p>
            <p><span>RAM:</span> {config.ram}</p>
            <p><span>Storage:</span> {config.storage}</p>
            {typeof config.singleCoreScore === "number" && (
              <p className="hardware-score-line">
                <span>Single-core score:</span> {config.singleCoreScore} or higher
              </p>
            )}
          </article>
        ))}
      </div>
    ) : (
      <p className="hardware-unavailable">Not currently available in this location.</p>
    )}
  </div>
);

export const Hardware = () => {
  const origin = typeof window !== "undefined" ? window.location.origin : "";
  const pageUrl = `${origin}/hardware`;
  const description = "View all FlintHost hardware configurations for each active location.";
  const [latencyByLocation, setLatencyByLocation] = useState({});
  const [isPinging, setIsPinging] = useState(false);

  const runLatencyCheck = useCallback(async () => {
    setIsPinging(true);
    const next = {};

    try {
      await Promise.all(hardwareByLocation.map(async (location) => {
        const measured = await measureLatency(location.probeUrl);
        next[location.location] = typeof measured === "number" && measured < 1200 ? measured : null;
      }));

      setLatencyByLocation(next);
    } finally {
      setIsPinging(false);
    }
  }, []);

  useEffect(() => {
    runLatencyCheck();
  }, [runLatencyCheck]);


  const sortedHardwareLocations = useMemo(() => {
    return [...hardwareByLocation].sort((a, b) => {
      const aLatency = latencyByLocation[a.location];
      const bLatency = latencyByLocation[b.location];

      if (typeof aLatency !== "number" && typeof bLatency !== "number") {
        return 0;
      }
      if (typeof aLatency !== "number") {
        return 1;
      }
      if (typeof bLatency !== "number") {
        return -1;
      }
      return aLatency - bLatency;
    });
  }, [latencyByLocation]);


  return (
    <div className="hardware-page">
      <Helmet>
        <title>FlintHost | Hardware by Location</title>
        <meta name="description" content={description} />
        <meta property="og:title" content="FlintHost | Hardware by Location" />
        <meta property="og:description" content={description} />
        <meta property="og:type" content="website" />
        <meta property="og:url" content={pageUrl} />
        <meta property="og:image" content={`${origin}/img/Logo2.png`} />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="FlintHost | Hardware by Location" />
        <meta name="twitter:description" content={description} />
        <meta name="twitter:image" content={`${origin}/img/Logo2.png`} />
      </Helmet>

      <section className="hardware-hero">
        <p className="hardware-eyebrow">Infrastructure</p>
        <h1>Hardware by location</h1>
        <p>
          Transparent node specs for every active region, including CPU single-core scores so you can quickly compare per-thread performance.
        </p>
      </section>

      <section className="hardware-grid">
        {sortedHardwareLocations.map((location) => (
          <div className="hardware-location-card" key={location.location}>
            <div className="hardware-location-head">
              <h2><img className="hardware-flag" src={location.flag} alt={`${location.location} flag`} /> {location.location}</h2>
              <span className="hardware-ping-pill">{typeof latencyByLocation[location.location] === "number" ? `Ping ${latencyByLocation[location.location]}ms` : "Ping unavailable"}</span>
            </div>
            <div className="hardware-location-tiers">
              <HardwareTier configs={location.configs} />
            </div>
          </div>
        ))}
      </section>

      <div className="hardware-actions">
        <button type="button" className="hardware-reping" onClick={runLatencyCheck} disabled={isPinging}>
          {isPinging ? "Re-pinging..." : "Re-ping locations"}
        </button>
      </div>

      <section className="hardware-notice">
        <h2>Performance flexibility promise</h2>
        <p>
          If you are not fully happy with your current node, our team can migrate you to another node in the same
          location when that option is available for your game plan. Single-core score is shown as a simple comparison
          metric, and values shown are minimums for that grouped platform (actual nodes can score higher).
        </p>
      </section>
    </div>
  );
};
